import React, { useState } from "react";

import { AlertCircle, ArrowRight, Loader2, X } from "lucide-react";

import Button from "../../Components/Button";
import Modal from "../../Components/Modal";
import type { LatestSelfAssessmentContent } from "../../Store/Reducers/AssessmentSlice";
import {
  formatAssessmentDateUtc,
  parseAssessmentDate,
} from "../../Utils/Helper/assessmentDateUtils";
import { toCompactLevelLabel } from "../../Utils/Helper/levelUtils";
import { buildStaleDaysLabel } from "../../Utils/Helper/selfAssessmentCurrency";
export type PortfolioAssessChoice = "carry_forward" | "re_assess";

export type {
  LatestSelfAssessmentContent,
  PriorSelfAssessmentMeta,
  PriorSelfAssessmentResponse,
} from "../../Store/Reducers/AssessmentSlice";

const formatShortDate = (value?: string | null): string =>
  formatAssessmentDateUtc(value, "US_LONG");

const daysBetween = (a: Date, b: Date) =>
  Math.floor((b.getTime() - a.getTime()) / 86400000);

const formatSubmittedMeta = (
  submittedAt?: string | null,
  serverDaysSince?: number | null
): string => {
  if (!submittedAt) return "";
  const d = parseAssessmentDate(submittedAt);
  if (!d) return formatShortDate(submittedAt);
  const ago = serverDaysSince ?? daysBetween(d, new Date());
  const agoLabel = ago <= 0 ? "Today" : `${ago} day${ago === 1 ? "" : "s"} ago`;
  return `Submitted ${formatShortDate(submittedAt)} · ${agoLabel}`;
};

const getLevelLabel = (code?: string | null): string => {
  if (!code) return "—";
  return /\d/.test(code) ? toCompactLevelLabel(code) : code;
};

type PortfolioAssessChoiceModalProps = {
  isOpen: boolean;
  isLoading?: boolean;
  accountName: string;
  latest: LatestSelfAssessmentContent | null;
  /** Cycle the user is about to take after CF/RA. */
  startingKind?: "team" | "formal";
  onClose: () => void;
  onContinue: (choice: PortfolioAssessChoice) => void;
};

const PortfolioAssessChoiceModal: React.FC<PortfolioAssessChoiceModalProps> = ({
  isOpen,
  isLoading = false,
  accountName,
  latest,
  startingKind = "team",
  onClose,
  onContinue,
}) => {
  const [choice, setChoice] = useState<PortfolioAssessChoice>("re_assess");

  const prior = latest?.response;
  const meta = latest?.self_assessment;
  const levelCode = prior?.responses?.maturity_level ?? null;
  const rationale = prior?.responses?.rationale ?? "";
  const staleBadge = buildStaleDaysLabel(latest);
  const priorOrigin =
    (latest?.origin || meta?.origin || "").toString().toLowerCase() === "formal"
      ? "formal"
      : "team";
  const priorLabel =
    priorOrigin === "formal" ? "formal assessment" : "team assessment";
  const submittedLine = [
    formatSubmittedMeta(prior?.submitted_at, latest?.input_days_since),
    priorLabel,
    "prior round",
  ]
    .filter(Boolean)
    .join(" · ");

  const bannerMessage =
    startingKind === "formal" ? (
      latest?.input_label_stale ? (
        <>
          Your input is {latest.input_days_since} days old but still counts this
          cycle. You can carry it forward or reassess.
        </>
      ) : (
        <>
          Your cadence is current for this account. You can carry it forward or
          reassess.
        </>
      )
    ) : (
      <>
        A prior assessment exists for this account. This starts a{" "}
        <strong>new team assessment round</strong>. Your teammates will be
        notified to submit their responses.
      </>
    );

  const carryForwardBlurb =
    startingKind === "formal"
      ? "Your prior level is still accurate. We'll submit it on this Formal round, linked to your last completed response. You can add an optional note."
      : "Your prior level is still accurate. We'll create a new team assessment dated today, linked to your last completed response. You can add an optional note.";

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="6xl"
      showHeader={false}
      className="h-[80vh] max-h-[90vh]"
      contentClassName="overflow-hidden flex flex-col h-full"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 pb-3">
          <div className="min-w-0">
            <h3 className="mb-0 text-lg font-semibold text-gray-900">
              Assess account: {accountName}
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Short assessment · approx. 5 min
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {staleBadge && !isLoading ? (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-600">
                {staleBadge}
              </span>
            ) : null}
            <button
              type="button"
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
            <p className="text-sm text-gray-500">Checking prior assessments…</p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="mt-4 flex gap-2.5 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
              <p className="m-0 text-sm leading-relaxed text-sky-950">
                {bannerMessage}
              </p>
            </div>

            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                  Prior assessment
                </p>
                {levelCode ? (
                  <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-[10px] font-semibold text-rose-700">
                    {getLevelLabel(levelCode)}
                  </span>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-gray-500">{submittedLine}</p>
              {rationale ? (
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <p className="whitespace-pre-wrap text-sm text-gray-800">
                    {rationale}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="mt-5">
              <p className="text-sm font-semibold text-gray-900">
                What would you like to do?
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setChoice("carry_forward")}
                  className={`rounded-lg border p-4 text-left transition ${
                    choice === "carry_forward"
                      ? "border-violet-500 bg-violet-50/40 ring-1 ring-violet-500"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <p className="text-sm font-bold text-gray-900">
                    {choice === "carry_forward" ? "✓ " : ""}
                    Carry Forward
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-gray-600">
                    {carryForwardBlurb}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setChoice("re_assess")}
                  className={`rounded-lg border p-4 text-left transition ${
                    choice === "re_assess"
                      ? "border-violet-500 bg-violet-50/40 ring-1 ring-violet-500"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <p
                    className={`text-sm font-bold ${
                      choice === "re_assess"
                        ? "text-violet-700"
                        : "text-gray-900"
                    }`}
                  >
                    {choice === "re_assess" ? "✓ " : ""}
                    Re-Assess
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-gray-600">
                    Things have changed. Open the form with your prior answers
                    pre-filled so you can update the level and rationale.
                  </p>
                </button>
              </div>
            </div>

            <div className="mt-auto flex items-center justify-end gap-3 border-t border-gray-200 pt-4 mt-6">
              <Button variant="outline-primary" onClick={onClose}>
                Cancel
              </Button>
              <button
                type="button"
                onClick={() => onContinue(choice)}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-violet-600 px-5 text-sm font-semibold text-white hover:bg-violet-700"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PortfolioAssessChoiceModal;
